import { cn } from '@/lib/utils';

/**
 * CylinderTankIcon — stylized tall-cylinder SVG: dome-topped body, small
 * valve cap with a yellow handle. Customer's count renders in a circle
 * mid-tank.
 *
 *   5LB CO2          → 'xs'
 *   10LB CO2         → 'sm'
 *   20LB CO2, Mix    → 'md'
 *   50LB CO2         → 'lg'
 *   Nitrogen size 60 → 'lg'
 *
 *   CO2 → light blue-grey
 *   Mix → light orange (beer gas)
 *   N2  → near-black
 */

type Tone = 'co2' | 'mix' | 'n2';
type Size = 'mini' | 'inline' | 'xs' | 'sm' | 'md' | 'lg';

interface ToneSpec {
  body: string;
  bodyShadow: string;
  outline: string;
  cap: string;
  valve: string;
  countText: string;
  countFill: string;
}

const TONE: Record<Tone, ToneSpec> = {
  co2: {
    body: '#bdc6d0',
    bodyShadow: '#8a98a6',
    outline: '#15243a',
    cap: '#ffffff',
    valve: '#fbbf24',
    countText: '#15243a',
    countFill: 'rgba(255,255,255,0.85)',
  },
  mix: {
    body: '#fdba74',
    bodyShadow: '#c2410c',
    outline: '#7c2d12',
    cap: '#ffffff',
    valve: '#fbbf24',
    countText: '#7c2d12',
    countFill: 'rgba(255,255,255,0.85)',
  },
  n2: {
    body: '#1f2937',
    bodyShadow: '#0b1220',
    outline: '#000000',
    cap: '#ffffff',
    valve: '#fbbf24',
    countText: '#ffffff',
    countFill: 'rgba(31,41,55,0.7)',
  },
};

const SIZE: Record<Size, { w: number; h: number; r: number; countFont: number }> = {
  mini:   { w: 22, h: 44,  r: 0,  countFont: 0  },
  inline: { w: 42, h: 96,  r: 0,  countFont: 0  },
  xs:     { w: 52, h: 110, r: 14, countFont: 14 },
  sm:     { w: 60, h: 130, r: 18, countFont: 18 },
  md:     { w: 70, h: 156, r: 22, countFont: 22 },
  lg:     { w: 82, h: 192, r: 26, countFont: 26 },
};

export function CylinderTankIcon({
  count,
  tone = 'co2',
  size = 'md',
  showCount,
  className,
}: {
  /** Number rendered in a white circle in the middle of the tank. Pass
   * undefined or set showCount={false} to render just the silhouette
   * for inline-with-text use. */
  count?: number;
  tone?: Tone;
  size?: Size;
  showCount?: boolean;
  className?: string;
}) {
  const t = TONE[tone];
  const s = SIZE[size];
  const renderCount =
    (showCount ?? size !== 'inline') && typeof count === 'number';

  // Tall slim cylinder with a small valve cap on top.
  const capW = s.w * 0.34;
  const capH = s.h * 0.075;
  const capX = (s.w - capW) / 2;
  const bodyTop = capH;
  const sidePad = s.w * 0.08;
  const bodyW = s.w - sidePad * 2;
  const domeR = bodyW / 2;
  const bottomR = bodyW * 0.18;
  const bodyBottom = s.h - 2;
  const cx = s.w / 2;
  const cy = bodyTop + (bodyBottom - bodyTop) / 2 + domeR * 0.2;

  const bodyPath =
    `M ${sidePad} ${bodyTop + domeR} ` +
    `Q ${sidePad} ${bodyTop} ${sidePad + domeR} ${bodyTop} ` +
    `L ${s.w - sidePad - domeR} ${bodyTop} ` +
    `Q ${s.w - sidePad} ${bodyTop} ${s.w - sidePad} ${bodyTop + domeR} ` +
    `L ${s.w - sidePad} ${bodyBottom - bottomR} ` +
    `Q ${s.w - sidePad} ${bodyBottom} ${s.w - sidePad - bottomR} ${bodyBottom} ` +
    `L ${sidePad + bottomR} ${bodyBottom} ` +
    `Q ${sidePad} ${bodyBottom} ${sidePad} ${bodyBottom - bottomR} ` +
    `Z`;

  const shadowPath =
    `M ${s.w - sidePad - bodyW * 0.18} ${bodyTop + domeR * 0.6} ` +
    `Q ${s.w - sidePad} ${bodyTop + domeR * 1.4} ${s.w - sidePad} ${bodyTop + domeR * 2.4} ` +
    `L ${s.w - sidePad} ${bodyBottom - bottomR} ` +
    `Q ${s.w - sidePad} ${bodyBottom} ${s.w - sidePad - bottomR} ${bodyBottom} ` +
    `L ${s.w - sidePad - bodyW * 0.16} ${bodyBottom} ` +
    `Z`;

  const valveStemW = Math.max(2, capW * 0.12);
  const valveCrossW = capW * 0.5;
  const valveCrossH = Math.max(2, capH * 0.16);

  return (
    <svg
      viewBox={`0 0 ${s.w} ${s.h}`}
      width={s.w}
      height={s.h}
      aria-hidden
      className={cn('block shrink-0 drop-shadow-md', className)}
    >
      <path
        d={bodyPath}
        fill={t.body}
        stroke={t.outline}
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
      <path d={shadowPath} fill={t.bodyShadow} opacity={0.55} />

      <rect
        x={capX}
        y={0.5}
        width={capW}
        height={capH}
        fill={t.cap}
        stroke={t.outline}
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
      <rect
        x={cx - valveStemW / 2}
        y={capH * 0.18}
        width={valveStemW}
        height={capH * 0.7}
        fill={t.valve}
        stroke={t.outline}
        strokeWidth={0.6}
      />
      <rect
        x={cx - valveCrossW / 2}
        y={capH * 0.32}
        width={valveCrossW}
        height={valveCrossH}
        fill={t.valve}
        stroke={t.outline}
        strokeWidth={0.6}
      />
      <rect
        x={cx - valveCrossW / 2}
        y={capH * 0.6}
        width={valveCrossW}
        height={valveCrossH}
        fill={t.valve}
        stroke={t.outline}
        strokeWidth={0.6}
      />

      {renderCount && (
        <>
          <circle
            cx={cx}
            cy={cy}
            r={s.r}
            fill={t.countFill}
            stroke={t.outline}
            strokeWidth={1.5}
          />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
            fontWeight={800}
            fontSize={s.countFont}
            fill={t.countText}
            style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}
          >
            {count}
          </text>
        </>
      )}
    </svg>
  );
}

/** Map a cylinder label to the right tank icon size + tone. */
export function tankFromLabel(label: string): { tone: Tone; size: Size } {
  switch (label) {
    case '5LB CO2':
      return { tone: 'co2', size: 'xs' };
    case '10LB CO2':
      return { tone: 'co2', size: 'sm' };
    case '20LB CO2':
      return { tone: 'co2', size: 'md' };
    case '50LB CO2':
      return { tone: 'co2', size: 'lg' };
    case 'Mix gas':
      return { tone: 'mix', size: 'md' };
    case 'Nitrogen':
      return { tone: 'n2', size: 'lg' };
    default:
      return { tone: 'co2', size: 'md' };
  }
}
